/**
 * Privacy Policy Page
 *
 * Displays privacy policy and data protection information for CPR Training Direct.
 * Placeholder content - to be replaced with actual legal text.
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Privacy Policy | CPR Training Direct</title>
        <meta name="title" content="Privacy Policy | CPR Training Direct" />
        <meta
          name="description"
          content="CPR Training Direct privacy policy. Learn how we collect, use, and protect your personal information."
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/privacy" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/privacy" />
        <meta property="og:title" content="Privacy Policy | CPR Training Direct" />
        <meta
          property="og:description"
          content="CPR Training Direct privacy policy. Learn how we collect, use, and protect your personal information."
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/privacy" />
        <meta name="twitter:title" content="Privacy Policy | CPR Training Direct" />
        <meta
          name="twitter:description"
          content="CPR Training Direct privacy policy. Learn how we collect, use, and protect your personal information."
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />
      </Helmet>

      {/* Hero Section - Brutalist */}
      <section className="bg-indigo_dye-500 text-white py-16 md:py-20 px-6">
        <div className="container mx-auto text-center">
          <h1
            data-aos="fade-up"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider mb-4"
          >
            Privacy Policy
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-mono text-lg text-indigo_dye-200 uppercase tracking-wide"
          >
            Last Updated: October 30, 2024
          </p>
        </div>
      </section>

      {/* Page Container */}
      <div className="bg-white min-h-screen py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <div data-aos="fade-up" className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 font-mono text-indigo_dye-600 hover:text-amaranth_purple-600 font-bold uppercase tracking-wide transition-colors"
              >
                ← Back to Home
              </Link>
            </div>

            {/* Content */}
            <div data-aos="fade-up" data-aos-delay="100" className="border-4 border-gray-400 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-8 md:p-12">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-0 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Introduction
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                CPR Training Direct ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Information We Collect
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Name, email address, and phone number</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Payment information for course registration</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Course completion and certification data</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Communications you send to us</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                How We Use Your Information
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Process your course registrations and payments</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Issue CPR certifications and maintain certification records</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Communicate with you about courses, renewals, and updates</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Improve our services and website functionality</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Comply with legal obligations and certification requirements</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Information Sharing
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Certification bodies (AHA, ASHI, Red Cross) as required</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Service providers who assist in operating our services</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Your employer, if they sponsored your training</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Law enforcement when required by law</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Data Security
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Your Rights
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Access and receive a copy of your personal information</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Correct inaccurate or incomplete information</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Request deletion of your information (subject to legal requirements)</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Opt-out of marketing communications</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Contact Us
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-indigo_dye-50 border-4 border-indigo_dye-200 p-4 mb-4">
                <p className="font-body text-gray-700">
                  <strong className="font-mono uppercase">CPR Training Direct</strong><br />
                  Contact us via our <Link to="/contact" className="text-indigo_dye-600 hover:text-amaranth_purple-600 font-semibold">contact page</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
